import {Box, FormControlLabel, IconButton, ListItem, Switch, TextField} from "@mui/material";
import React, {useContext, useReducer, useState} from "react";
import {Add, Delete, Edit} from "@mui/icons-material";
import {Cursor, ErrorRegisterNotFound} from "../../fstn/cursor";
import {Network} from "../../fstn/network";
import {connect} from "react-redux";
import {selectFocus, selectProject} from "../../redux/selectors/editor";
import RegisterDetails from "./RegisterDetails";
import {Register} from "../../fstn/register";
import {FirebaseContext} from "../firebase/FirebaseProvider";
import {FloatingDialog} from "../dialogs/FloatingDialog";

interface IProps {
    cursor: Cursor | undefined
    focus: string
    network: Network | undefined
    onClose: () => void
    project: string
}

function RegisterDialog({cursor, network, onClose, project}: IProps) {
    const [, forceUpdate] = useReducer(time => time = Date.now(), 0);
    const [detailsKey, setDetailsKey] = useState("");
    const [details, setDetails] = useState<Register | undefined>(undefined);
    const {removeRegister, setRegister} = useContext(FirebaseContext);
    const handleRegisterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        try {
            const register = event.currentTarget.dataset.register;

            if (cursor && register) {
                const currentRegister = cursor.getRegister(register);

                if (currentRegister.type === "number") {
                    const newValue = Number.parseInt(event.currentTarget.value);

                    if (! Number.isNaN(newValue)) {
                        cursor.setRegister(register, newValue);
                    }
                } else {
                    cursor.setRegister(register, event.currentTarget.value);
                }
                // Force redraw as cursor object hides update
                forceUpdate();
            }
        } catch (exception) {
            if (exception instanceof ErrorRegisterNotFound) {
                console.error(exception.message);
            } else {
                throw exception;
            }
        }
    }
    const toggleRegisterValue = (event: React.ChangeEvent<{}>, checked: boolean) => {
        const target = event.target as unknown as {id: string};
        const register = target.id;

        if (cursor && register) {
            cursor.setRegister(register, checked);
            forceUpdate();
        }
    };
    const references: {[register: string]: string []} = {};
    const handleAddRegister = () => {
        if (network) {
            const registerNames = Array.from(network.registers.keys());
            let register = "New Register";
            let index = 0;

            while (registerNames.includes(register)) {
                register = `New Register ${index++}`
            }
            setRegister(project, register, {type: "number", value: 0});
        }
    };
    const handleDeleteRegister = (event: React.MouseEvent<HTMLElement>) => {
        removeRegister(project, event.currentTarget.dataset.register as string);
    };
    const handleClickDetails = (event: React.MouseEvent<HTMLElement>) => {
        if (network) {
            const registerKey = event.currentTarget.dataset.register as string;
            const register = network.registers.get(registerKey);

            if (register !== undefined) {
                setDetails(register);
                setDetailsKey(registerKey);
            }
        }
    };
    const handleCloseDetails = () => {
        setDetails(undefined);
        setDetailsKey("");
    }

    network?.registers.forEach((register, registerKey) => {
        references[registerKey] = [];
    });
    network?.states.forEach((state, stateKey) => {
        network.registers.forEach((register, registerKey) => {
            if (state.actions.some(action => 
                Object.keys(action.parameters).some(key => action.parameters[key].reference === registerKey)
            )) {
                references[registerKey].push(stateKey);
            }
        });
    });
    return (
        <FloatingDialog title="Registers" tools={
            <IconButton size="small" edge="end" color="inherit" onClick={handleAddRegister}>
                <Add/>
            </IconButton>
        } onClose={onClose}>
            <Box>
                {Array.from(network?.registers.keys() || []).map(register => {
                    const details = network!.registers.get(register) as Register;
                    const initialValue = details.get();
                    const currentValue = cursor?.hasRegister(register) ? cursor.getRegisterValue(register) : initialValue;
                    const modifyEnabled = references[register].length === 0;
                    const title = modifyEnabled ? "Delete register" :
                        `Register referenced by ${references[register].length} states, it cannot be changed`;

                    return (
                        <ListItem key={register}>
                            {details.type === "boolean" ?
                                <FormControlLabel control={
                                    <Switch checked={currentValue === true} id={register}/>
                                } label={register} style={{flexGrow: 1}} onChange={toggleRegisterValue}/>
                                :
                                <TextField
                                    label={register} value={currentValue} fullWidth
                                    inputProps={{"data-register": register}}
                                    error={currentValue !== initialValue} size="small"
                                    type={details.type} onChange={handleRegisterChange}/>
                            }
                            <div title={title}>
                                <IconButton size="small" data-register={register} onClick={handleClickDetails}>
                                    <Edit/>
                                </IconButton>
                            </div>
                            <div title={title}>
                                <IconButton
                                    size="small" disabled={!modifyEnabled} data-register={register}
                                    onClick={handleDeleteRegister}>
                                    <Delete/>
                                </IconButton>
                            </div>
                        </ListItem>
                    )})}
                {details !== undefined && <RegisterDetails
                    register={details} registerKey={detailsKey} onClose={handleCloseDetails}/>
                }
            </Box>
        </FloatingDialog>
    );
}
const mapStateToProps = (store: never) => ({
    focus: selectFocus(store),
    project: selectProject(store)
});

export default connect(mapStateToProps, {
})(RegisterDialog);
